import type { fabric } from "fabric";
import type { CanvasPaperConfig } from "../../designer/PageDesigner/PageDesigner";

interface CalculatedPageBreak {
  updated: boolean;
  newTop: number;
}

/**
 * Calculate the top position of an object with page break.
 *
 * The top position of the object is calculated based on the current page and the page break margin.
 * The page break margin is the space before and after the page break line, which an object cannot cross.
 *
 * If the object hits the page break, the new top position is calculated based on the next page. An object
 * hits the page break if the top position plus the height of the object is greater than the page height
 * minus the page break margin. The new top position is the top position of the next page plus the page break
 * margin.
 *
 * If the object does not hit the page break, the new top position is the same as the current top position.
 *
 * @param object Dimensions of the object (top and height)
 * @param paperConfig The paper configuration
 * @returns CalculatedPageBreak object with the new top position and if the object hits the page break
 */
export function calculateTopWithPageBreak(
  object: Pick<fabric.Object, "top" | "height">,
  paperConfig: CanvasPaperConfig,
): CalculatedPageBreak {
  const { top = 0, height = 0 } = object;
  const { pageBreakMargin = 0 } = paperConfig;

  const newTop = adjustElementTopWithPageBreak(
    top,
    height,
    paperConfig.height,
    pageBreakMargin,
  );

  return {
    updated: newTop !== top,
    newTop,
  };
}

export function adjustElementTopWithPageBreak(
  elementTop: number,
  elementHeight: number,
  pageHeight: number,
  pageBreakMargin: number,
): number {
  const lineBottom = elementTop + elementHeight;
  const pageIndex = Math.floor(elementTop / pageHeight);
  const pageTop = pageHeight * pageIndex;
  const pageBottom = pageTop + pageHeight;
  const pageTopWithMargin = pageTop + pageBreakMargin;
  const pageBottomWithoutMargin = pageBottom - pageBreakMargin;

  const hitsTopMarginPage =
    elementTop > pageTop && elementTop < pageTopWithMargin;
  if (hitsTopMarginPage) {
    return pageTopWithMargin;
  }

  const hitsLineTopOfMarginBottomPage =
    elementTop > pageBottomWithoutMargin && elementTop < pageBottom;
  const hitsLineBottomOfMarginBottomPage =
    lineBottom > pageBottomWithoutMargin && elementTop < pageBottom;

  if (hitsLineTopOfMarginBottomPage || hitsLineBottomOfMarginBottomPage) {
    const nextPageTop = pageHeight * (pageIndex + 1);
    return nextPageTop + pageBreakMargin;
  }

  return elementTop;
}
